<template>
    <div class="hailuo-model-page relative xl:pt-[6rem] pt-[3rem]">
        <video class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] object-cover z-0 pointer-events-none"
            :poster="heroPoster"
            :src="demoVideos.heroBackground" autoplay muted loop
            preload="metadata" playsinline webkit-playsinline></video>
        <div class="absolute top-0 left-0 w-full h-screen xl:h-[1080px] bg-black/20 pointer-events-none z-[1]"></div>
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section relative z-10">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <div class="page-name animate-float backdrop-blur-md">
                                <span class="flex h-1.5 w-1.5 relative">
                                    <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-brand-neon"></span>
                                    <span
                                        class="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-neon opacity-75"></span>
                                </span>
                                <span>
                                    {{
                                        t('common.model_online', {
                                            model: 'Hailuo 2.3',
                                        })
                                    }}
                                </span>
                            </div>
                            <h1>{{ t('hailuo23.generatorTitle') }}</h1>
                            <p class="generator-description !text-white" v-html="t('hailuo23.generatorDescription')">
                            </p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <!-- PC 端显示 NewVideoGenerator -->
                    <div class="hidden md:block">
                        <NewVideoGenerator model-name="Hailuo 2.3" :tabs="tabs" redirect-url="/ai-video"
                            @generate="handleGenerate" model-key="hailuo-2-3">
                            <template #model-logo>
                                <div class="hailuo-logo">H</div>
                            </template>
                        </NewVideoGenerator>
                    </div>
                    <!-- 移动端显示 MobileGenerator -->
                    <div class="block md:hidden">
                        <MobileGenerator type="video" model-name="Hailuo 2.3" model-key="hailuo-2-3"
                            default-tab="image" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section relative z-10 !bg-transparent">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title mt-[2rem]">
                        {{ t('hailuo23.coreFeaturesTitle') }}
                    </h2>
                </div>
                <div class="features-showcase-container grid grid-cols-1 xl:grid-cols-2 gap-[80px]">
                    <div v-for="feature in coreFeatures" :key="feature.title" class="feature-card">
                        <div class="flex flex-col">
                            <div class="feature-video">
                                <VideoPlayer
                                    :poster="getResourceThumbnail(feature.url)"
                                    :src="feature.url"
                                    :title="feature.title"
                                    :description="feature.description"
                                />
                            </div>
                            <div class="feature-text !px-0">
                                <h3 class="feature-title">
                                    {{ feature.title }}
                                </h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('hailuo23.howToUseTitle') }}
                    </h2>
                    <h3 class="text-text-adad text-base">
                        {{ t('common.three_step') }}
                    </h3>
                </div>
                <ol class="gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    <li v-for="step in howToUseStepsList" :key="step.title"
                        class="step-card flex flex-col items-center justify-center gap-5 min-h-[150px] md:min-h-[200px] lg:min-h-[270px] border border-white/10 rounded-[20px] p-5">
                        <img :src="step.icon" :alt="step.title" class="step-icon w-[96px] h-[96px]" />
                        <div class="step-content">
                            <h3 class="text-[20px] font-bold text-white">
                                {{ step.title }}
                            </h3>
                            <p v-html="step.description" class="text-sm text-text-adad"></p>
                        </div>
                    </li>
                </ol>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <section class="youtube-videos-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('hailuo23.youtubeTitle') }}
                    </h2>
                </div>

                <div class="youtube-carousel">
                    <button class="carousel-nav-btn carousel-nav-left" @click="previousVideo"
                        :aria-label="t('common.previous')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>

                    <div class="videos-wrapper">
                        <div class="videos-scroll" :style="{
                            transform: `translateX(-${currentVideoIndex * (100 / 3)}%)`,
                        }">
                            <div v-for="(video, index) in youtubeVideos" :key="index" class="video-iframe-container">
                                <iframe :src="video.embedUrl" :title="video.title" class="video-iframe" frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowfullscreen></iframe>
                            </div>
                        </div>
                    </div>

                    <button class="carousel-nav-btn carousel-nav-right" @click="nextVideo"
                        :aria-label="t('common.next')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </section>

        <!-- X Posts Section -->
        <TwitterPosts :title="t('hailuo23.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('hailuo23.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/hailuo-ai" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/2b9d9924fb8b25932b804c7fe99d792d.png"
                                :alt="t('hailuo23.hailuo02Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('hailuo23.hailuo02Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/sora/sora-2" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                :alt="t('hailuo23.sora2Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('hailuo23.sora2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/wan-ai" class="model-card !bg-[#22242D]">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/88fca6eaf44c1b7c1e02caa0a061fe11.png"
                                :alt="t('hailuo23.wanTitle')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('hailuo23.wanTitle') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('hailuo23.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI logo" class="logo-image" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('hailuo23.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('hailuo23.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('hailuo23.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('hailuo23.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import '~/assets/css/howToUse.css'
import { getResourceThumbnail } from '~/utils/common'
import MobileGenerator from '~/components/mobile/MobileGenerator.vue'
import { useSettingVideoSEO } from '~/composables/useSettingVideoSEO'
const { t } = useI18n()

const demoVideos = {
    heroBackground:
        'https://static.weryai.com/growth/video/weryai_0bc946f5460c4dcc7a9af9e629c5923a.mp4',
    dynamicControl:
        'https://static.weryai.com/growth/video/8a80fd00a823265e6f41062a6d983820.mp4',
    styleSupport:
        'https://static.weryai.com/growth/video/b75ac28ad1d61f4c842b68104a55290c.mp4',
    microExpression:
        'https://static.weryai.com/growth/video/48243cbf21a83fdd9b0ce62bbb1bb3d1.mp4',
    objectMotion:
        'https://static.weryai.com/growth/video/9b78ffeee7d943c70a77d2fd9e501f8a.mp4',
} as const

// 移动端检测
const isMobile = ref(false)
const MOBILE_BREAKPOINT = 768

const checkIsMobile = () => {
    if (import.meta.client) {
        isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
    }
}

// 防抖处理 resize 事件
let resizeTimer: ReturnType<typeof setTimeout> | null = null
const handleResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
        checkIsMobile()
    }, 150)
}
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'hailuo_23' })

const heroPoster = computed(() =>
    getResourceThumbnail(
        'https://static.weryai.com/growth/image/weryai_5f8706d3d8f4a4a196595d6b98b03ad6.png'
    )
)

useHead({
    link: [
        {
            rel: 'preload',
            as: 'image',
            href: heroPoster.value,
        },
    ],
})

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('hailuo23.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// Structured Data for SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: t('hailuo23.generatorTitle'),
                description: t('hailuo23.pageDescription'),
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
                provider: {
                    '@type': 'Organization',
                    name: '微鲤AI',
                    url: 'https://www.weryai.com.cn',
                },
                featureList: [
                    t('hailuo23.dynamicControlTitle'),
                    t('hailuo23.styleSupportTitle'),
                    t('hailuo23.microExpressionTitle'),
                    t('hailuo23.objectMotionTitle'),
                ],
            }),
        },
    ],
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('hailuo23.tabImageToVideo'), external: false, value: 'image' },
    { name: t('hailuo23.tabTextToVideo'), external: false, value: 'text' },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

// HowTo Steps for SEO
const howToUseSteps = computed(() => [
    {
        title: t('hailuo23.step1Title'),
        description: t('hailuo23.step1Description'),
    },
    {
        title: t('hailuo23.step2Title'),
        description: t('hailuo23.step2Description'),
    },
    {
        title: t('hailuo23.step3Title'),
        description: t('hailuo23.step3Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('hailuo23.howToUseTitle')),
    howToUseSteps
)

// YouTube Videos Data
const currentVideoIndex = ref(0)

const youtubeVideos = ref([
    {
        title: 'Hailuo 2.3 - Complete Overview',
        embedUrl: 'https://www.youtube.com/embed/JnyB2P-UZhY',
    },
    {
        title: 'Hailuo 2.3 Tutorial Guide',
        embedUrl: 'https://www.youtube.com/embed/X_mKwJUAbMw',
    },
    {
        title: 'Hailuo 2.3 Features Explained',
        embedUrl: 'https://www.youtube.com/embed/Xy2VtdxqSJQ',
    },
    {
        title: 'Getting Started with Hailuo 2.3',
        embedUrl: 'https://www.youtube.com/embed/xvaVjvU1g9M',
    },
    {
        title: 'Hailuo 2.3 Advanced Tips',
        embedUrl: 'https://www.youtube.com/embed/cggAnq4_mDA',
    },
    {
        title: 'Hailuo 2.3 Best Practices',
        embedUrl: 'https://www.youtube.com/embed/X_mKwJUAbMw',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://x.com/Robotson_AI/status/1981259376037155276',
    'https://x.com/opener_ai/status/1981632619109601542',
    'https://x.com/umesh_ai/status/1981931234797535433',
    'https://x.com/BrentLynch/status/1981109333871251954',
    'https://x.com/azed_ai/status/1981416810382840212',
    'https://x.com/Uncanny_Harry/status/1981187866266128401',
    'https://x.com/yachimat_manga/status/1981247333682626938',
    'https://x.com/seirdotmk/status/1982171084498571500',
]

// Carousel Methods
const nextVideo = () => {
    // Maximum 3 visible videos, so max index is 2 (3rd video)
    const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
    if (currentVideoIndex.value >= maxVisibleIndex) {
        currentVideoIndex.value = 0 // Back to first video
    } else {
        currentVideoIndex.value++
    }
}

const previousVideo = () => {
    if (currentVideoIndex.value <= 0) {
        // Back to last visible video position
        const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
        currentVideoIndex.value = maxVisibleIndex
    } else {
        currentVideoIndex.value--
    }
}

// Page Mounted
onMounted(() => {
    // Page view analytics tracking
    analyticsEvent('page_view_hailuo23')
    analyticsEvent('view_model_page_common', {
        model_name: 'hailuo_23',
        type: 'video',
    })

    // 初始化移动端检测
    checkIsMobile()
    window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
    // 清理 resize 事件监听器
    window.removeEventListener('resize', handleResize)
    if (resizeTimer) clearTimeout(resizeTimer)
})

// FAQ Data
const faqData = computed(() => [
    {
        question: t('hailuo23.faq1Question'),
        answer: t('hailuo23.faq1Answer'),
    },
    {
        question: t('hailuo23.faq2Question'),
        answer: t('hailuo23.faq2Answer'),
    },
    {
        question: t('hailuo23.faq3Question'),
        answer: t('hailuo23.faq3Answer'),
    },
    {
        question: t('hailuo23.faq4Question'),
        answer: t('hailuo23.faq4Answer'),
    },
    {
        question: t('hailuo23.faq5Question'),
        answer: t('hailuo23.faq5Answer'),
    },
    {
        question: t('hailuo23.faq6Question'),
        answer: t('hailuo23.faq6Answer'),
    },
    {
        question: t('hailuo23.faq7Question'),
        answer: t('hailuo23.faq7Answer'),
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

const coreFeatures = computed(() => [
    {
        title: t('hailuo23.dynamicControlTitle'),
        description: t('hailuo23.dynamicControlDescription'),
        url: demoVideos.dynamicControl,
    },
    {
        title: t('hailuo23.styleSupportTitle'),
        description: t('hailuo23.styleSupportDescription'),
        url: demoVideos.styleSupport,
    },
    {
        title: t('hailuo23.microExpressionTitle'),
        description: t('hailuo23.microExpressionDescription'),
        url: demoVideos.microExpression,
    },
    {
        title: t('hailuo23.objectMotionTitle'),
        description: t('hailuo23.objectMotionDescription'),
        url: demoVideos.objectMotion,
    },
])

const howToUseStepsList = computed(() => [
    {
        title: t('hailuo23.step1Title'),
        description: t('hailuo23.step1Description'),
        icon: 'https://static.weryai.com/cms/weryai_d235e92179a57a96bd38dc29336cc6bf.webp',
    },
    {
        title: t('hailuo23.step2Title'),
        description: t('hailuo23.step2Description'),
        icon: 'https://static.weryai.com/cms/weryai_3585be709806892f4d0a51e8b53d4c20.webp',
    },
    {
        title: t('hailuo23.step3Title'),
        description: t('hailuo23.step3Description'),
        icon: 'https://static.weryai.com/cms/weryai_e637ad7b5e3ed566325b8a9be194bfd2.webp',
    },
])

useSettingVideoSEO({
    key: 'hailuo23-videoobject',
    title: t('hailuo23.generatorTitle'),
    description: t('hailuo23.pageDescription'),
    contentUrl: demoVideos.heroBackground,
    thumbnailUrl: heroPoster.value,
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';

/* 移动端样式覆盖 */
@media (max-width: 768px) {
    .container {
        padding: 0 16px !important;
    }

    .generator-container {
        padding: 0 !important;
        max-width: 100% !important;
    }

    /* 功能特性卡片样式 */
    .feature-card {
        border-radius: 16px;
        border: 1px solid rgba(255, 255, 255, 0.05);
        background: #120E11;
        padding: 16px;
        overflow: hidden;
    }

    .features-showcase-container {
        gap: 16px !important;
    }

    /* 如何使用部分 - 移动端横向布局 */
    .how-to-use-section ol {
        gap: 12px !important;
    }

    .how-to-use-section ol li.step-card {
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 16px !important;
        min-height: auto !important;
        padding: 16px !important;
        border-radius: 16px !important;
    }

    /* 第1个和第2个卡片样式 */
    .how-to-use-section ol li.step-card:nth-child(1),
    .how-to-use-section ol li.step-card:nth-child(2) {
        border: 1px solid rgba(255, 255, 255, 0.10) !important;
        background: rgba(255, 255, 255, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    /* 第3个卡片样式 */
    .how-to-use-section ol li.step-card:nth-child(3) {
        border: 1px solid rgba(255, 255, 255, 0.10) !important;
        background: rgba(153, 247, 104, 0.05) !important;
        backdrop-filter: blur(6px);
    }

    .how-to-use-section .step-icon {
        width: 64px !important;
        height: 64px !important;
        flex-shrink: 0;
    }

    .how-to-use-section .step-content {
        flex: 1;
        text-align: left;
    }

    .how-to-use-section .step-content h3 {
        font-size: 16px !important;
        text-align: left !important;
        margin-bottom: 4px;
    }

    .how-to-use-section .step-content p {
        text-align: left !important;
        margin: 0;
    }
}
</style>
